import { createMutationHook } from "@accurx/api/QueryClient";
import {
    AddContactRequest,
    CreateContactResponse,
} from "@accurx/api/clinician-messaging";
import { HttpError, httpClient2 } from "@accurx/shared";
import { ContactFormFieldErrors } from "domains/directory/schemas/contact-form/ContactFormSchema";
import { z } from "zod";

export const CreateContactKey = "CreateContact";

type CreateContactArgs = {
    workspaceId: number;
} & AddContactRequest;

export const useCreateContact = createMutationHook<
    CreateContactArgs,
    CreateContactResponse
>(() => ({
    mutationKey: [CreateContactKey],
    mutationFn: async ({ workspaceId, ...payload }) => {
        try {
            return await httpClient2
                .post({
                    path: "/api/clinicianmessaging/directory/:workspaceId/contacts",
                    params: {
                        workspaceId,
                    },
                    json: payload,
                })
                .json<CreateContactResponse>();
        } catch (e) {
            if (e instanceof HttpError && e.statusCode === 400) {
                const data = ContactCreationErrorResponseSchema.parse(
                    await e.response.json(),
                );

                throw new ContactCreationError(
                    "Could not create contact",
                    data,
                );
            }
            throw e;
        }
    },
}));

export class ContactCreationError extends Error {
    fieldErrors: ContactFormFieldErrors;

    constructor(message: string, fieldErrors: ContactFormFieldErrors) {
        super(message);
        this.fieldErrors = fieldErrors;
    }
}

const PhoneNumberError =
    "Phone number is too long or contains invalid characters";
const WebsiteError = "Website url is either invalid or not secure";

const getFieldErrors = (errorMessage: string): ContactFormFieldErrors => {
    if (errorMessage === PhoneNumberError) {
        return { phoneNumber: [PhoneNumberError] };
    }

    if (errorMessage === WebsiteError) {
        return { websiteUrl: [WebsiteError] };
    }

    return { phoneNumber: [PhoneNumberError], websiteUrl: [WebsiteError] };
};

const ContactCreationErrorResponseSchema = z
    .object({
        message: z.string(),
    })
    .transform(({ message }) => getFieldErrors(message));
